import { IMAGES } from "../assets";
import { useNavigate, useSearchParams } from "react-router-dom";
import { STRINGS } from "../constant";
import { SHOW_TOAST } from "../constant/utils";
import { useEffect, useState, useContext } from "react";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { emailRegex } from "../constant/utils";
import { acceptInvite } from "../api/activation";
import Loader from "../components/loader";
import { getAppLogo } from "../api/theme_settings";
import { ThemeContext } from "../context/ThemeProvider";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faKey, faEye, faEyeSlash, faUser } from '@fortawesome/free-solid-svg-icons';
import { getClientId } from "../api/client";
import InfoDialog from "../components/InfoDialog";
import { getThemeTextColor } from "../utils/themeUtils";
import theme_config from "../../config";
import { createLogger } from "../utils/logger";
import { AuthContext } from "../context/AuthProvider";

export default function ActivationSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { loggingEnabled } = useContext(AuthContext);
  const logger = createLogger(loggingEnabled);
  
  // Get values from URL parameters
  const urlParams = {
    activationCode: searchParams.get('activation_code'),
    firstName: searchParams.get('first_name'),
    lastName: searchParams.get('last_name'),
    email: searchParams.get('email')
  };

  const userDetails = (() => {
    try {
      const storedUser = localStorage.getItem("user");
      logger.log('>>>>>>>>>>>>>>>> storedUser', storedUser);
      
      return storedUser ? JSON.parse(storedUser) : {};
    } catch (error) {
      logger.error("Error parsing user details from localStorage:", error);
      return {};
    }
  })();
  const [clientId, setClientId] = useState(null);
  const { theme, currentTheme, setCurrentTheme } = useContext(ThemeContext);
  const isDark = currentTheme === "dark";

  useEffect(() => {
      const fetchDefaultTheme = async () => {
        const mode = theme_config.default_theme_mode;
    
        if (mode) {
          const root = document.documentElement;

          if (mode === "dark") {
            root.classList.add("dark");
            root.classList.remove("light");
          } else if (mode === "light") {
            root.classList.add("light");
            root.classList.remove("dark");
          }
          
          setCurrentTheme(mode);
        }
      };
    
      fetchDefaultTheme();
    }, []);

  useEffect(() => {
    const getClient = async () => {
      try {
        logger.log("Attempting to get client ID...");
        const id = await getClientId();
        logger.log("Client ID obtained:", id);
        setClientId(id);
      } catch (error) {
        logger.error("Error getting client:", error);
        
        // Provide more specific error messages
        let errorMessage = "Error getting client information. Please try again.";
        if (error.message.includes('permission-denied')) {
          errorMessage = "Access denied. Please check your authentication or contact support.";
        } else if (error.message.includes('unavailable')) {
          errorMessage = "Service temporarily unavailable. Please try again later.";
        } else if (error.message.includes('network')) {
          errorMessage = "Network error. Please check your connection and try again.";
        }
        
        SHOW_TOAST(errorMessage, "error");
        
        // Don't navigate to login immediately, let user see the error
        // navigate("/login");
      }
    };

    getClient();
  }, [navigate]);

  const handleSubmit = async (values) => {
    if (!clientId) {
      setErrorMessage("Client information not available. Please try again.");
      setShowErrorModal(true);
      return;
    }

    logger.log('Activation attempt with:', {
      clientId,
      activationCode: values.activationCode,
      email: values.email
    });

    try {
      if (typeof clientId === 'object' && clientId.then) {
        logger.error('Client ID is still a Promise!');
        setErrorMessage("Client information is not ready. Please try again.");
        setShowErrorModal(true);
        return;
      }

      // Prevent any default navigation
      const result = await acceptInvite({
        clientId: clientId,
        activation_code: values.activationCode,
        user_id: values.email,
        pwd: values.password,
        firstName: values.firstName || "",
        lastName: values.lastName || "",
      });
        
      if (!result.success) {
        logger.error('Activation failed:', result);
        setErrorMessage(result.message || "Activation failed. Please check your activation code and try again.");
        setShowErrorModal(true);
      } else {
        // Show success toast and navigate only after successful activation
        SHOW_TOAST(result.message, "success");
        // Use replace instead of push to prevent back navigation
        navigate("/prepare", { replace: true });
      }
    } catch (error) {
      logger.error("Error during activation:", error);
      if (error.code === 'internal') {
        setErrorMessage("The activation service is currently unavailable. Please try again later or contact support.");
      } else {
        setErrorMessage("An error occurred during activation. Please try again.");
      }
      setShowErrorModal(true);
    }
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters"),
    lastName: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters"),
    email: Yup.string()
      .matches(emailRegex, "Invalid email address")
      .required("Please enter the email"),
    password: Yup.string()
      .required("Please enter the password")
      .min(6, "At least 6 characters")
      .matches(/^\S*$/, "No spaces")
      .matches(/[A-Z]/, "At least one uppercase")
      .matches(/[a-z]/, "At least one lowercase")
      .matches(/\d/, "At least one digit")
      .matches(/[@$!%*?&]/, "At least one special char"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Please confirm your password"),
    activationCode: Yup.string()
      .matches(/^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$/, "Format XXXX‑XXXX‑XXXX")
      .required("Activation code is required"),
  });

  const [logo, setLogo] = useState(null);
  useEffect(() => { getAppLogo(currentTheme).then(setLogo); }, []);

  const bgStart =
    theme?.background?.gradientStart ||
    (isDark ? "hsl(210,30%,8%)" : "hsl(210,10%,95%)");
  const bgEnd =
    theme?.background?.gradientEnd ||
    (isDark ? "hsl(210,30%,12%)" : "hsl(210,10%,90%)");
  const glassBg = isDark
    ? theme?.card?.light
    : theme?.card?.dark;
  const glassBorder = isDark
    ? theme?.border?.dark
    : theme?.border?.light;
  const txt1 = theme?.text?.login_primary || (isDark ? "#fff" : "#111");
  const txt2 = theme?.text?.login_secondary || (isDark ? "rgba(255,255,255,0.75)" : "#555");
  const muted = theme?.text?.muted || (isDark ? "#888" : "#999");
  const linkCol = theme?.text?.link || "#0066cc";
  const inpBd = theme?.input?.border || (isDark ? "#555" : "#ddd");
  const errCol = theme?.status?.error || "#dc3545";
  const focusRing = theme?.primary?.main || "#0066cc";
  const btnBg = theme?.button?.primary?.background || "#0066cc";
  const btnText = theme?.button?.primary?.text || "#fff";
  const radius = "1rem";
  
  return (
    <div>
      <div
        className="relative min-h-screen flex items-center justify-center px-4 login-bg"
        style={{
          background: `linear-gradient(135deg, ${bgStart}, ${bgEnd})`,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div
          className="w-full max-w-lg p-10 space-y-20 transition-opacity transform"
          style={{
            background: glassBg,
            backdropFilter: "blur(12px)",
            border: `0px solid ${glassBorder}`,
            borderRadius: radius,
          }}
        >
          {/* logo & header */}
          <div className="text-center space-y-2">
            <img
              src={logo ?? IMAGES.logo}
              alt="Logo"
              className="mx-auto h-12 mb-10"
            />
            <h1
              className="text-3xl font-semibold"
              style={{ color: txt1, fontFamily: "'Poppins', sans-serif" }}
            >
              Create Your Account
            </h1>
            <p className="italic text-sm" style={{ color: txt2 }}>
              Get started with your certification journey
            </p>
          </div>

          {/* form */}
          <Formik
            initialValues={{
              firstName: urlParams.firstName || userDetails.firstName || "",
              lastName: urlParams.lastName || userDetails.lastName || "",
              email: urlParams.email || userDetails.email || "",
              password: "",
              confirmPassword: "",
              activationCode: urlParams.activationCode || userDetails.licenseKey || "",
            }}
            onSubmit={async (values, { setSubmitting }) => {
setSubmitting(true);
await handleSubmit(values);
setSubmitting(false);
}}
            validationSchema={validationSchema}
          >
            {({ values, errors, handleBlur, handleChange, handleSubmit, isSubmitting }) => {
              return (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  logger.log('Form submitted');
                  handleSubmit(e);
                }} className="space-y-8">
                  {[
                    { key: "firstName",       icon: faUser,         placeholder: "First Name" },
                    { key: "lastName",        icon: faUser,         placeholder: "Last Name" },
                    { key: "email",           icon: faEnvelope,     placeholder: "Email" },
                    { key: "password",        icon: faLock,         placeholder: "Password" },
                    { key: "confirmPassword", icon: faLock,         placeholder: "Confirm Password" },
                    { key: "activationCode",  icon: faKey,          placeholder: "Activation Code (XXXX‑XXXX‑XXXX)" },
                  ].map(({key, icon, placeholder}) => {
                    const isPwd = key.includes("password") || key === "confirmPassword";
                    const show   = key==="password" ? showPassword
                                 : key==="confirmPassword" ? showConfirmPassword
                                 : false;
                    const toggle = () => key==="password"
                      ? setShowPassword(ps => !ps)
                      : setShowConfirmPassword(ps => !ps);

                    return (
                      <div key={key} className="relative">
                        <FontAwesomeIcon
                          icon={icon}
                          className="absolute left-0 top-1/2 -translate-y-1/2 text-lg"
                          style={{ color: txt2, opacity: 0.6 }}
                        />
                        <input
                          type={ isPwd
                            ? (show ? "text" : "password")
                            : "text"
                          }
                          name={key}
                          id={key}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values[key]}
                          placeholder={placeholder}
                          className="w-full pl-10 py-3 bg-transparent placeholder-opacity-60 focus:outline-none"
                          style={{
                            border: "none",
                            borderBottom: `1px solid ${errors[key] ? errCol : inpBd}`,
                            caretColor: focusRing,
                            color: txt1,
                          }}
                        />
                        {isPwd && (
                          <button
                            type="button"
                            onClick={toggle}
                            className="absolute right-0 top-1/2 -translate-y-1/2 p-2"
                            style={{ color: txt2 }}
                          >
                            <FontAwesomeIcon
                              icon={show ? faEye : faEyeSlash}
                            />
                          </button>
                        )}
                        <ErrorMessage
                          name={key}
                          component="div"
                          className="mt-1 text-xs"
                          style={{ color: errCol }}
                        />
                      </div>
                    );
                  })}

                  <div className="pt-10">
                    <div className="flex justify-center">
                      <button
                        type="submit"
                        className="w-3/4 py-5 font-semibold uppercase rounded-full transition-transform active:scale-95"
                        style={{
                          backgroundColor: btnBg,
                          color: btnText,
                          letterSpacing: "1px",
                        }}
                      >
                        {isSubmitting ? "Activating…" : "Activate Account"}
                      </button>
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>

          {/* login link */}
          <div className="pt-4 text-center">
            <div
              className="h-px mb-4"
              style={{ backgroundColor: inpBd }}
            />
            <p className="text-sm" style={{ color: muted }}>
              {STRINGS.already_have_account}{" "}
              <span
                onClick={() => navigate("/login")}
                className="font-medium cursor-pointer hover:underline"
                style={{ color: linkCol }}
              >
                {STRINGS.signin_register}
              </span>
            </p>
          </div>
        </div>
      </div>
      <InfoDialog
        isOpen={showErrorModal}
        title="Error"
        description={errorMessage}
        actions={[
          {
            label: "Try Again",
            onClick: () => setShowErrorModal(false),
            className: "bg-lz-blue text-white hover:bg-lz-orange"
          }
        ]}
      />
    </div>
  );
}
